import React from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styled from 'styled-components';
import { Body5 } from '@ui';

const CellWrapper = styled.div`
  min-height: 28px;
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 3px;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${({ theme }) => theme.themeColors.primaryLowOpacity};
  }
`;

const DisplayValue = styled(Body5)`
  color: ${({ $type, theme }) => {
    if ($type === 'number') return theme.themeColors.primaryText || '#4a9eff';
    if ($type === 'boolean') return '#d4a017';
    if ($type === 'null') return theme.themeColors.secondaryText || '#999';
    return theme.themeColors.text;
  }};
  font-style: ${({ $type }) => ($type === 'null' ? 'italic' : 'normal')};
  word-break: break-word;
`;

const EditTextarea = styled.textarea`
  width: 100%;
  padding: 4px 8px;
  font-size: 13px;
  font-family: inherit;
  line-height: 1.4;
  border: 1px solid ${({ theme }) => theme.themeColors.primaryColor || '#4a9eff'};
  border-radius: 3px;
  outline: none;
  background: ${({ theme }) => theme.themeColors.component};
  color: ${({ theme }) => theme.themeColors.text};
  resize: none;
  overflow: hidden;

  &:focus {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.themeColors.primaryLowOpacity};
  }
`;

const parseValuePreservingType = (inputStr, originalValue) => {
  if (typeof originalValue === 'number') {
    const num = Number(inputStr);
    return isNaN(num) ? originalValue : num;
  }
  if (typeof originalValue === 'boolean') {
    const lower = inputStr.toLowerCase().trim();
    if (lower === 'true') return true;
    if (lower === 'false') return false;
    return originalValue;
  }
  if (originalValue === null) {
    if (inputStr === '' || inputStr.toLowerCase().trim() === 'null') return null;
    return originalValue;
  }
  return inputStr;
};

const formatDisplayValue = (value) => {
  if (value === null) return 'null';
  if (typeof value === 'boolean') return value.toString();
  if (typeof value === 'string' && value === '') return '(empty)';
  return String(value);
};

const getValueType = (value) => {
  if (value === null) return 'null';
  return typeof value;
};

const MARKDOWN_PATTERN =
  /(\*\*.+?\*\*|__.+?__|_.+?_|\*[^*]+\*|~~.+?~~|^#{1,6}\s|^[-*+]\s|^\d+\.\s|^>\s|\[.+?\]\(.+?\)|`[^`]+`|^---$|^\|.+\|)/m;

const containsMarkdown = (value) => {
  if (typeof value !== 'string') return false;
  return MARKDOWN_PATTERN.test(value);
};

const MarkdownWrapper = styled.div`
  font-size: 13px;
  line-height: 1.5;
  word-break: break-word;
  color: ${({ theme }) => theme.themeColors.text};

  p {
    margin: 0 0 4px 0;
    &:last-child {
      margin-bottom: 0;
    }
  }

  strong {
    font-weight: 600;
  }

  em {
    font-style: italic;
  }

  code {
    font-family: 'SF Mono', 'Consolas', 'Monaco', monospace;
    font-size: 12px;
    background: ${({ theme }) => theme.themeColors.componentNoOpacity || '#f0f0f0'};
    padding: 1px 4px;
    border-radius: 3px;
  }

  ul,
  ol {
    margin: 2px 0;
    padding-left: 20px;
  }

  li {
    margin: 1px 0;
  }

  a {
    color: ${({ theme }) => theme.themeColors.primaryColor || '#4a9eff'};
    text-decoration: underline;
  }

  blockquote {
    margin: 4px 0;
    padding-left: 10px;
    border-left: 3px solid ${({ theme }) => theme.themeColors.lowContrastBorder || '#e0e0e0'};
    color: ${({ theme }) => theme.themeColors.secondaryText || '#999'};
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: 4px 0;
  }

  th,
  td {
    padding: 4px 8px;
    border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder || '#e0e0e0'};
    text-align: left;
    vertical-align: top;
  }

  th {
    background: ${({ theme }) => theme.themeColors.componentNoOpacity || '#f8f8f8'};
    font-weight: 600;
  }

  h1 {
    font-size: 20px;
    margin: 4px 0 2px 0;
    line-height: 2;
  }

  h2 {
    font-size: 18px;
    margin: 4px 0 2px 0;
    line-height: 2;
  }

  h3 {
    font-size: 16px;
    margin: 4px 0 2px 0;
    line-height: 2;
  }

  h4,
  h5,
  h6 {
    font-size: 13px;
    margin: 4px 0 2px 0;
    line-height: 2;
  }
`;

const autoResize = (el) => {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = el.scrollHeight + 'px';
};

const TruncatedRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
`;

const ExpandBadge = styled.button`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  padding: 1px 5px;
  font-size: 10px;
  line-height: 1.4;
  border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder || '#ddd'};
  border-radius: 3px;
  background: ${({ theme }) => theme.themeColors.componentNoOpacity || '#f5f5f5'};
  color: ${({ theme }) => theme.themeColors.secondaryText || '#888'};
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.themeColors.primaryLowOpacity || '#e8e8e8'};
    color: ${({ theme }) => theme.themeColors.text};
  }
`;

const TRUNCATE_LENGTH = 6;

export const EditableCell = ({ value, onUpdate, collapseSignal }) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [editValue, setEditValue] = React.useState('');
  const [localTruncated, setLocalTruncated] = React.useState(false);
  const textareaRef = React.useRef(null);
  const displayRef = React.useRef(null);
  const prevVersionRef = React.useRef(collapseSignal?.version);

  React.useEffect(() => {
    if (collapseSignal && collapseSignal.version !== prevVersionRef.current) {
      prevVersionRef.current = collapseSignal.version;
      setLocalTruncated(collapseSignal.collapsed);
    }
  }, [collapseSignal]);

  const handleStartEdit = () => {
    setEditValue(value === null ? '' : String(value));
    setIsEditing(true);
  };

  React.useEffect(() => {
    if (isEditing && textareaRef.current) {
      autoResize(textareaRef.current);
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, [isEditing]);

  const handleChange = (e) => {
    setEditValue(e.target.value);
    autoResize(e.target);
  };

  const handleSave = () => {
    const newValue = parseValuePreservingType(editValue, value);
    setIsEditing(false);
    setLocalTruncated(false);
    if (newValue !== value) {
      onUpdate(newValue);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    }
    if (e.key === 'Escape') {
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <EditTextarea
        ref={textareaRef}
        value={editValue}
        rows={1}
        onChange={handleChange}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
      />
    );
  }

  const displayStr = formatDisplayValue(value);
  const isMarkdown = containsMarkdown(value);

  const shouldTruncate = localTruncated && typeof value === 'string' && value.length > TRUNCATE_LENGTH;
  const truncatedStr = shouldTruncate ? value.slice(0, TRUNCATE_LENGTH) + '…' : displayStr;

  const handleExpand = (e) => {
    e.stopPropagation();
    setLocalTruncated(false);
  };

  return (
    <CellWrapper ref={displayRef} onClick={handleStartEdit}>
      {shouldTruncate ? (
        <TruncatedRow>
          <DisplayValue $type={getValueType(value)}>{truncatedStr}</DisplayValue>
          <ExpandBadge onClick={handleExpand}>[{value.length}]</ExpandBadge>
        </TruncatedRow>
      ) : isMarkdown ? (
        <MarkdownWrapper>
          <Markdown remarkPlugins={[remarkGfm]}>{displayStr}</Markdown>
        </MarkdownWrapper>
      ) : (
        <DisplayValue $type={getValueType(value)}>{displayStr}</DisplayValue>
      )}
    </CellWrapper>
  );
};
